import React, { useState, useEffect } from 'react';
import { fetchUserVehicles } from '../services/api';

// Map vehicle names to their corresponding icons
const vehicleIconMap = {
  'Forklift Type 1(R)': '/icon/forklift.png',
  'Forklift Type 2(R)': '/icon/forklift.png',
  'MTV(L)': '/icon/MTVIconWhite.png'
};

function Sidebar({ selectedVehicle, onVehicleSelect, closeMobileMenu, userPK = '1', onResultClick, currentPage = 'dashboard' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user vehicles when component mounts
  useEffect(() => {
    const getUserVehicles = async () => {
      try {
        setLoading(true);
        // Fetch vehicles for user with the provided UserPK
        const data = await fetchUserVehicles(userPK);
        
        // Transform the vehicle data to match our component format
        const formattedVehicles = data.vehicles.map(vehicle => ({
          name: vehicle.ScreenName,
          customIcon: vehicleIconMap[vehicle.ScreenName] || '/icons/two-wheeler.png', // Default icon if not found
          availableAssessments: vehicle.AvailableAssessments,
          maximumViolation: vehicle.MaximumViolation
        }));
        
        setVehicles(formattedVehicles);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch vehicles:', err);
        setError('Failed to load mobile equipment. Please try again later.');
        setLoading(false);
      }
    };

    getUserVehicles();
  }, [userPK]);

  const handleSelectClick = (e) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const handleVehicleSelect = (ScreenName) => {
    onVehicleSelect(ScreenName);
    setIsOpen(false);
    if (closeMobileMenu) closeMobileMenu();
  };

  const handleResultClick = () => {
    if (onResultClick) {
      onResultClick();
    }
    if (closeMobileMenu) closeMobileMenu();
  };

  const handleDashboardClick = () => {
    // Navigate back to main dashboard page
    window.location.href = '/';
    if (closeMobileMenu) closeMobileMenu();
  };

  return (
    <div className="h-full text-white overflow-y-auto glossy-light relative"
         style={{
           backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.15) 100%), url('/SIDEBG.jpg')`,
           backgroundSize: 'cover',
           backgroundPosition: 'center',
           backgroundRepeat: 'no-repeat',
           borderRadius: '0'
         }}>
      <div className="flex flex-col h-full">
        {/* Close button - visible only on mobile */}
        <button 
          onClick={closeMobileMenu}
          className="absolute top-4 right-4 md:hidden text-white hover:text-gray-200 z-10 drop-shadow-lg"
          aria-label="Close menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        
        <div className="flex flex-col items-center w-full p-4 mt-12">
          {/* Dashboard Heading as a button */}
          <button 
            onClick={handleDashboardClick}
            className="text-lgwhio sm:text-2xl font-bold text-white mb-10 tracking-wide drop-shadow-lg hover:text-yellow-200 transition-colors duration-200 border-none bg-transparent cursor-pointer"
          >
            DASHBOARD
          </button>
          
          {/* Wrapper for dropdown and result button with fixed height space */}
          <div className="w-full flex flex-col flex-grow justify-between">
            {/* Container for vehicle selector and dropdown - Only show on dashboard page */}
            {currentPage === 'dashboard' && (
              <div className="relative w-full mb-4">
                {loading ? (
                  <div className="w-full py-3 sm:py-4 px-4 rounded-lg bg-black/10 text-white text-center drop-shadow">
                    Loading mobile equipment...
                  </div>
                ) : error ? (
                  <div className="w-full py-3 sm:py-4 px-4 rounded-lg bg-red-500/20 text-white text-center drop-shadow">
                    {error}
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleSelectClick}
                      className="relative w-full py-4 sm:py-5 px-4 rounded-lg text-white
                               text-xs sm:text-sm font-medium mb-2 text-center cursor-pointer
                               transition-all duration-300 ease-in-out hover:bg-white/20
                               focus:outline-none focus:ring-0 bg-white/10"
                    >
                      <span className="block w-full text-center pr-6 px-5">{selectedVehicle || 'Mobile Equipment Type'}</span>
                      <span 
                        className="absolute right-4 top-1/2 -translate-y-1/2 transform transition-transform duration-300" 
                        style={{ transform: isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%) rotate(0deg)' }}
                      >
                        ▼
                      </span>
                    </button>
                    
                    {/* Dropdown container with fixed position */}
                    <div 
                      className={`absolute w-full bg-black/10 rounded-lg shadow-lg z-20 overflow-hidden transition-all duration-300 ease-in-out ${
                        isOpen 
                          ? 'opacity-100 visible' 
                          : 'opacity-0 invisible'
                      }`}
                      style={{
                        maxHeight: isOpen ? '200px' : '0',
                        transform: isOpen ? 'translateY(0)' : 'translateY(-10px)'
                      }}
                    >
                      <div className="overflow-y-auto" style={{ maxHeight: '200px' }}>
                        {vehicles.length === 0 ? (
                          <div className="px-4 py-4 text-white text-center drop-shadow">
                            No mobile equipment found for this user
                          </div>
                        ) : (
                          vehicles.map((vehicle, index) => (
                            <div
                              key={index}
                              onClick={() => handleVehicleSelect(vehicle.name)}
                              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-black/20 transition-all duration-200 text-white
                                         transform hover:translate-x-1 animate-slideIn drop-shadow"
                              style={{ 
                                animationDelay: `${index * 50}ms`
                              }}
                            >
                              <img 
                                src={vehicle.customIcon} 
                                alt={vehicle.name}
                                className="w-6 h-6 object-contain filter brightness-0 invert transition-transform duration-300 hover:scale-110"
                              />
                              <div className="flex flex-col">
                                <span className="text-xs font-medium">{vehicle.name}</span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Result button with animated positioning - moves with dropdown */}
            <div 
              className="w-full mt-auto transition-all duration-300 ease-in-out"
              style={{ 
                transform: currentPage === 'dashboard' && isOpen ? 'translateY(180px)' : 'translateY(0)',
                marginTop: '0.5rem'
              }}
            >
              <button 
                onClick={handleResultClick}
                className="w-full py-4 sm:py-5 px-3 sm:px-4 bg-white/10 rounded-lg border-none 
                         text-white text-xs sm:text-sm font-semibold cursor-pointer
                         hover:bg-white/20 transition-all duration-300 ease-in-out hover:shadow-lg drop-shadow-lg
                         flex items-center justify-center gap-2"
              >
                <img 
                  src="/icon/REPORT.png"
                  alt="Report Icon"
                  className="w-5 h-5 object-contain filter brightness-0 invert"
                />
                Result
              </button>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .bg-primary-dark {
          background-color: rgba(0, 0, 0, 0.15);
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default Sidebar;